/**
 * Clerk laedt Teile seines Client-Bundles erst bei Bedarf nach — unter anderem
 * beim Verify-Schritt des Logins. Auf Mobilgeraeten mit Netzwechsel laeuft
 * dieser Nachladeversuch regelmaessig in einen Timeout.
 *
 * Belegt in `error_logs`: 433 Faelle als `unhandledrejection`, davon 319
 * Timeouts. Die Dateien existieren — ein direkter Abruf liefert 200, und eine
 * unmittelbare Wiederholung nach einem Fehlschlag ebenfalls. Deshalb genuegt
 * ein Retry; ein harter Reload waere schaedlich, weil er den laufenden
 * Login-Versuch verwirft und der Nutzer einen neuen Code anfordern muesste.
 *
 * Analyse: plans/proxy-messung-anleitung.md
 */

/** Erkennt fehlgeschlagene Bundle-Nachladungen (webpack und Vite). */
export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const message =
    typeof err === "string"
      ? err
      : typeof (err as { message?: unknown }).message === "string"
        ? ((err as { message: string }).message)
        : "";
  const name = (err as { name?: string }).name ?? "";

  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  );
}

type RetryOptions = {
  /** Zusaetzliche Versuche nach dem ersten. Zwei genuegen — mehr verlaengert
   *  nur die Wartezeit, ohne die Erfolgsquote messbar zu erhoehen. */
  retries?: number;
  /** Wartezeit vor dem ersten Retry in ms; verdoppelt sich danach. */
  delayMs?: number;
  /** Erscheint in den Konsolen-Meldungen, damit erkennbar ist, welcher
   *  Schritt betroffen war (verify, resend, …). */
  label?: string;
  /**
   * Wird gerufen, wenn ein Retry den Aufruf gerettet hat oder endgueltig
   * gescheitert ist.
   *
   * Ohne das waere ein geretteter Login von einem reibungslosen nicht zu
   * unterscheiden: In `error_logs` landet nur, was ganz scheitert. Die Frage
   * "haben Nutzer noch Probleme?" liesse sich sonst nicht beantworten — man
   * saehe bloss, dass niemand mehr scheitert, nicht ob das Bundle weiterhin
   * bei jedem dritten Mobilnutzer hakt.
   *
   * Als Callback statt direktem `trackEvent`, damit dieses Modul nicht von
   * der Analytics-Schicht abhaengt und in Tests ohne sie nutzbar bleibt.
   */
  onRetryOutcome?: (outcome: {
    /** `true` = ein spaeterer Versuch hat geklappt, `false` = alle gescheitert. */
    recovered: boolean;
    /** Wie viele Versuche insgesamt noetig waren (1 = kein Retry). */
    attempts: number;
    durationMs: number;
    label: string;
    chunkUrl: string;
  }) => void;
};

/**
 * Holt die Chunk-URL aus der Fehlermeldung. Webpack schreibt sie im Klartext
 * hinein: "Loading chunk 9573 failed. (error: https://…/ui-common_….js)".
 * Ohne sie ist in der Konsole nicht erkennbar, welche Datei gehangen hat.
 */
function extractChunkUrl(err: unknown): string {
  const message =
    typeof err === "string"
      ? err
      : typeof (err as { message?: unknown })?.message === "string"
        ? (err as { message: string }).message
        : "";
  return message.match(/https?:\/\/[^\s)]+/)?.[0] ?? "unbekannt";
}

/**
 * Fuehrt `fn` aus und wiederholt **nur** bei Chunk-Ladefehlern.
 *
 * Alles andere — falscher Code, abgelaufener Code, bereits angemeldet — wird
 * sofort weitergereicht. Ein Retry waere dort sinnlos und wuerde bei
 * "already signed in" den Fehler sogar verstaerken.
 */
export async function withChunkRetry<T>(
  fn: () => Promise<T>,
  { retries = 2, delayMs = 400, label = "clerk", onRetryOutcome }: RetryOptions = {},
): Promise<T> {
  let lastError: unknown;
  const started = Date.now();
  let lastChunkUrl = "unbekannt";

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const result = await fn();
      if (attempt > 0) {
        onRetryOutcome?.({
          recovered: true,
          attempts: attempt + 1,
          durationMs: Date.now() - started,
          label,
          chunkUrl: lastChunkUrl,
        });
        console.info(
          `[chunk-retry] ${label}: erfolgreich im Versuch ${attempt + 1}/${retries + 1} ` +
            `nach ${Date.now() - started} ms`,
        );
      }
      return result;
    } catch (err) {
      lastError = err;

      if (!isChunkLoadError(err)) {
        // Fachlicher Fehler — kein Retry, absichtlich. Nicht geloggt, sonst
        // rauscht jeder Tippfehler beim Code durch die Konsole.
        throw err;
      }

      const chunkUrl = extractChunkUrl(err);
      lastChunkUrl = chunkUrl;
      if (attempt === retries) {
        onRetryOutcome?.({
          recovered: false,
          attempts: attempt + 1,
          durationMs: Date.now() - started,
          label,
          chunkUrl,
        });
        console.error(
          `[chunk-retry] ${label}: Bundle konnte nach ${retries + 1} Versuchen ` +
            `nicht geladen werden (${Date.now() - started} ms). Datei: ${chunkUrl}`,
        );
        throw err;
      }

      const wait = delayMs * 2 ** attempt;
      console.warn(
        `[chunk-retry] ${label}: Bundle-Ladung fehlgeschlagen ` +
          `(Versuch ${attempt + 1}/${retries + 1}), erneuter Versuch in ${wait} ms. ` +
          `Datei: ${chunkUrl}`,
      );
      await new Promise((r) => setTimeout(r, wait));
    }
  }

  throw lastError;
}
